'use client';

import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslations } from 'next-intl';
import { Container, HStack, Text, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import { IRedux } from '@/_store/types';
import { ClockIcon } from '@/_assets/icon-components';
import { isWorkingHour } from '@/_utils/isWorkingHour';
import ConfirmModal from '@/_components/ConfirmModal';
import { sessionActions } from '@/_store/session/session.slice';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import FutureTimePicker from '../FutureTimePicker';

interface WorkingHours {
  start: string;
  end: string;
}

const AlertWorkingHours = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { session, common, future_time, settings } = useSelector(
    (state: IRedux) => ({
      session: state.session,
      common: state.common,
      future_time: state.checkout?.future_time,
      settings: state.settings,
    }),
    shallowEqual,
  );

  const { order_type, permissions, offHours } = session;
  const { branches } = common;

  const t = useTranslations();
  const dispatch = useDispatch();
  const shipperId = useActiveBrand();
  const { open, onOpen, onClose } = useDisclosure();

  const showPreorder =
    settings?.data?.data?.show_preorder ||
    typeof settings?.data?.data?.show_preorder == 'undefined';
  const branch = branches?.[shipperId];

  const workingHours = useMemo<WorkingHours | null>(() => {
    if (
      branch?.work_hour_start &&
      branch?.work_hour_end &&
      !isWorkingHour(branch.work_hour_start, branch.work_hour_end)
    ) {
      return { start: branch.work_hour_start, end: branch.work_hour_end };
    }
    if (
      session?.shipper?.work_hour_start &&
      session?.shipper?.work_hour_end &&
      !isWorkingHour(
        session?.shipper?.work_hour_start,
        session?.shipper?.work_hour_end,
      )
    ) {
      return {
        start: session?.shipper?.work_hour_start,
        end: session?.shipper?.work_hour_end,
      };
    }
    return null;
  }, [branches, session?.shipper]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (
        branch?.work_hour_start &&
        branch?.work_hour_end &&
        !future_time &&
        !isWorkingHour(branch.work_hour_start, branch.work_hour_end)
      ) {
        setIsOpen(true);
        return;
      }

      if (
        !branch?.id &&
        order_type?.type === 'delivery' &&
        !future_time &&
        permissions?.geolocation === 'accepted'
      ) {
        setIsOpen(true);
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [branches, future_time, permissions?.geolocation, order_type]);

  const handleOffHours = (shouldOpenTime: boolean) => {
    if (shouldOpenTime) {
      onOpen();
    } else {
      setIsOpen(false);
    }
    dispatch(sessionActions.setOffHours());
  };

  const message = workingHours
    ? order_type?.type === 'delivery'
      ? t(
          showPreorder
            ? 'only_in_home_page.delivery_starts_working'
            : 'only_in_home_page.delivery_starts_working_no_preorder',
          {
            start: workingHours.start,
            end: workingHours.end,
          },
        )
      : t(
          showPreorder
            ? 'only_in_home_page.establishment_starts_working'
            : 'only_in_home_page.establishment_starts_working_no_preorder',
          {
            start: workingHours.start,
            end: workingHours.end,
          },
        )
    : '';

  if (branch?.is_work_24_7) {
    return null;
  }

  return (
    <>
      {/* Banner Alert */}
      {workingHours && (
        <HStack bg='dark' p={4} mt={{ base: 0, lg: 5 }} rounded={16} gap='10px'>
          <ClockIcon />
          <Text fontSize='sm' color='light' w='full'>
            {message}
          </Text>
        </HStack>
      )}
      <ConfirmModal
        title={t('attention')}
        isOpen={isOpen && !offHours}
        onConfirm={() => handleOffHours(true)}
        onClose={() => handleOffHours(false)}
        description={
          t('we_are_currently_closed') +
          ' ' +
          (showPreorder ? t('do_you_preorder') : '')
        }
        text={{
          confirm: showPreorder ? 'pre_order' : undefined,
          cancel: 'menu',
        }}
      />
      <FutureTimePicker
        onOpen={onOpen}
        onClose={onClose}
        open={open}
        showTrigger={false}
      />
    </>
  );
};

export default AlertWorkingHours;
